import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import WhatsAppFloat from '../components/WhatsAppFloat';
import { blogPosts } from '../data/blogData';

const BlogDetail = () => {
    const { slug } = useParams();
    const navigate = useNavigate();
    const [post, setPost] = useState(null);
    const [relatedPosts, setRelatedPosts] = useState([]);

    useEffect(() => {
        const foundPost = blogPosts.find(p => p.slug === slug);
        if (!foundPost) {
            navigate('/404');
            return;
        }
        setPost(foundPost);

        // Find 3 related posts (excluding current)
        const others = blogPosts.filter(p => p.slug !== slug).slice(0, 3);
        setRelatedPosts(others);

        window.scrollTo(0, 0);

        const spinner = document.getElementById('spinner');
        if (spinner) {
            spinner.classList.remove('show');
            spinner.style.display = 'none';
        }
        if (window.WOW) {
            new window.WOW().init();
        }
    }, [slug, navigate]);

    if (!post) return null; // Wait for initial render

    // Compose Dual Schema (BlogPosting + FAQPage)
    const schema = [
        {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "image": [`https://webbears.com${post.image}`],
            "datePublished": post.date,
            "dateModified": post.date,
            "author": [{
                "@type": "Person",
                "name": post.author,
                "url": "https://webbears.com/team"
            }],
            "publisher": {
                "@type": "Organization",
                "name": "WebBears",
                "logo": {
                    "@type": "ImageObject",
                    "url": "https://webbears.com/img/images/fulllogowebbears.png"
                }
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": post.faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        }
    ];

    return (
        <>
            <SEO
                title={`${post.title} | WebBears Blog`}
                description={post.description}
                url={`https://webbears.com/blog/${post.slug}`}
                image={`https://webbears.com${post.image}`}
                type="article"
                schemaMarkup={schema}
            />
            <WhatsAppFloat />
            <Navbar />

            {/* Custom Breadcrumb Header */}
            <div className="container-fluid page-header mb-5">
                <div className="container mt-5 pt-5 text-center">
                    <h1 className="display-5 mb-3" style={{ maxWidth: '800px', margin: '0 auto' }}>{post.title}</h1>
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb justify-content-center mb-0">
                            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
                            <li className="breadcrumb-item"><Link to="/blog">Blog</Link></li>
                            <li className="breadcrumb-item active text-truncate" aria-current="page" style={{ maxWidth: '200px' }}>
                                {post.title}
                            </li>
                        </ol>
                    </nav>
                </div>
            </div>

            {/* Blog Content Layout */}
            <div className="container-xxl py-5">
                <div className="container">
                    <div className="row g-5">

                        {/* Main Body */}
                        <div className="col-lg-8">
                            <div className="mb-4 pb-3 border-bottom d-flex justify-content-between text-muted">
                                <span><i className="far fa-calendar-alt text-primary me-2"></i> {new Date(post.date).toLocaleDateString()} </span>
                                <span><i className="fa fa-user text-primary me-2"></i> {post.author} </span>
                            </div>

                            <img className="img-fluid rounded w-100 mb-5 shadow-sm" src={post.image} alt={post.title} />

                            <div
                                className="blog-content html-block"
                                dangerouslySetInnerHTML={{ __html: post.content }}
                                style={{ lineHeight: '1.8', fontSize: '1.1rem' }}
                            />

                            {/* FAQ Rendering Visually */}
                            <div className="mt-5 pt-4 border-top">
                                <h3 className="mb-4">Frequently Asked Questions</h3>
                                <div className="accordion" id="faqAccordion">
                                    {post.faqs.map((faq, index) => (
                                        <div className="accordion-item" key={index}>
                                            <h4 className="accordion-header" id={`heading${index}`}>
                                                <button className="accordion-button collapsed fw-bold" type="button" data-bs-toggle="collapse" data-bs-target={`#collapse${index}`}>
                                                    {faq.question}
                                                </button>
                                            </h4>
                                            <div id={`collapse${index}`} className="accordion-collapse collapse" data-bs-parent="#faqAccordion">
                                                <div className="accordion-body">
                                                    {faq.answer}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                        </div>

                        {/* Sidebar */}
                        <div className="col-lg-4">
                            <div className="bg-light p-4 text-center rounded mb-5 shadow-sm">
                                <h4 className="mb-3">Expert Consultation</h4>
                                <p>Ready to transform your business digitally? Let our expert engineers build your vision.</p>
                                <a href="https://wa.me/918408800193" className="btn btn-success w-100 py-3 mb-2"><i className="fab fa-whatsapp me-2"></i> Chat with Us</a>
                                <Link to="/contact" className="btn btn-outline-primary w-100 py-3">Submit Requirement</Link>
                            </div>

                            {/* Related Blogs */}
                            <div className="mt-5">
                                <h4 className="mb-4 border-bottom pb-2">Related Articles</h4>
                                {relatedPosts.map((related) => (
                                    <div className="d-flex mb-4 bg-light rounded overflow-hidden shadow-sm border" key={related.slug}>
                                        <img src={related.image} className="img-fluid" style={{ width: '100px', objectFit: 'cover' }} alt={related.title} />
                                        <div className="p-3 w-100 d-flex flex-column justify-content-center">
                                            <Link to={`/blog/${related.slug}`} className="h6 m-0 text-decoration-none text-dark d-block text-truncate" style={{ maxWidth: '180px' }} title={related.title}>
                                                {related.title}
                                            </Link>
                                            <small className="text-muted mt-1">{new Date(related.date).toLocaleDateString()}</small>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default BlogDetail;
